/**
 * Точная пользовательская семантика карточки аномалии (сценарии 1–5).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ
 * Тексты карточки должны различать состояния, которые до этого выглядели одинаково:
 * отсутствующая масса, числовой ноль, подтверждённая порожность и вагон, оформленный
 * как перевозимый груз на своих осях. Общий форматтер `weight-format.ts` обслуживает
 * ещё и таблицу реестра аномалий, поэтому его вывод не трогаем — здесь стоит
 * anomaly-specific presenter поверх его же статуса.
 *
 * ЧТО ЗДЕСЬ ДОКАЗАНО (Фаза 1, источники — только на чтение)
 *  • `row.cargoEtsng` = `import_etsng`, `row.cargoGng` = `import_gng`
 *    (transit-ai/scripts/build_anomaly_cases_v3.py:217–218) — то есть коды груза в строке
 *    кейса относятся к ИМПОРТНОЙ ноге;
 *  • `canonical_import_cargo_key = COALESCE('G:'||import_gng, 'E:'||import_etsng)`,
 *    `canonical_export_cargo_key` — то же для экспорта,
 *    `canonical_transition_key = canonical_import_cargo_key || '>' || canonical_export_cargo_key`
 *    (build_pair_corpus_v2.py:150–151, 194–196). ЕТСНГ участвует в ключе только тогда,
 *    когда ГНГ отсутствует;
 *  • «prior»-окно в наборах примеров — это строго предшествующие записи по метке времени
 *    экспортной ноги (`export_pair_date < case_available_at`), то есть включая более
 *    ранние записи текущего года (strict-prior refresh
 *    `v1anomrefresh_20260826_53855f642a988513`; anomaly-service/case-evidence-examples.mjs);
 *  • одна строка исторического набора = одна строка корпуса сопоставленных пар (`pair_id`):
 *    ОДНА импортная запись + ОДНА экспортная запись. Это не подтверждённая перевозка и не
 *    кейс реестра, поэтому пользовательский термин — «связка «импорт → экспорт»».
 *
 * ЧЕГО ЗДЕСЬ НЕТ
 * Ни одного вычисления показателя. Модуль ничего не считает, не заменяет null нулём и
 * не выводит порожность из отсутствия массы: в payload карточки аномалии отдельного
 * подтверждающего признака порожности нет вовсе, поэтому статус «Порожний» карточка не
 * показывает никогда.
 */
import type { AnomalyCaseRow } from './anomaly-cases-v3.types'
import { formatWeightDifference } from './weight-format'

/* ── коды груза: что именно является ключом сравнения ─────────────────────────── */

/** «ГНГ 86050000» либо «ЕТСНГ 421034» — ровно тот код, который попадает в ключ. */
export function importCargoKeyLabel(row: Pick<AnomalyCaseRow, 'cargoEtsng' | 'cargoGng'>): string | null {
  if (row.cargoGng) return `ГНГ ${row.cargoGng}`
  if (row.cargoEtsng) return `ЕТСНГ ${row.cargoEtsng}`
  return null
}

/** Оба кода импортной ноги, как они показаны в «Деталях кейса». */
export function importCargoCodesLabel(row: Pick<AnomalyCaseRow, 'cargoEtsng' | 'cargoGng'>): string | null {
  const parts: string[] = []
  if (row.cargoEtsng) parts.push(`ЕТСНГ ${row.cargoEtsng}`)
  if (row.cargoGng) parts.push(`ГНГ ${row.cargoGng}`)
  return parts.length ? parts.join(' / ') : null
}

/**
 * «G:99220000» → «ГНГ 99220000», «E:421034» → «ЕТСНГ 421034».
 *
 * Ровно тот префикс, который ставит `build_pair_corpus_v2.py`
 * (`COALESCE('G:'||gng, 'E:'||etsng)`). Неизвестный формат возвращается как есть —
 * выдумывать классификатор нельзя.
 */
export function decodeCargoKeyPart(part: string): string {
  const m = /^([GE]):(.+)$/.exec(part.trim())
  if (!m) return part.trim()
  return `${m[1] === 'G' ? 'ГНГ' : 'ЕТСНГ'} ${m[2]}`
}

/**
 * Ключ группы вариантов в читаемом виде.
 *
 * `canonical_transition_key` — «ГНГ 44101130 → ГНГ 99220000»;
 * `canonical_export_cargo_key` — «ГНГ 99220000».
 * Это ЕДИНСТВЕННОЕ, что реально различает группы: названия груза у разных вариантов
 * совпадают сплошь и рядом, поэтому заголовок обязан показывать коды.
 */
export function decodeVariantGroupKey(groupKey: string | null | undefined): string | null {
  const raw = (groupKey ?? '').trim()
  if (!raw) return null
  return raw.split('>').map(decodeCargoKeyPart).join(' → ')
}

/** Из какого кода собран ключ отбора — нужно, чтобы подпись не обещала лишнего. */
export const CARGO_KEY_RULE =
  'Ключом служит ГНГ; если ГНГ в строке отсутствует, ключом становится ЕТСНГ.'

/**
 * Точный состав «текущей комбинации» для показателей, сравнивающих переход груза.
 * Соответствует `canonical_transition_key` — и ничему больше.
 */
export const TRANSITION_KEY_TOOLTIP =
  'Комбинация сравнивается по паре кодов груза: код груза импортной ноги → код груза ' +
  `экспортной ноги. ${CARGO_KEY_RULE} Станции, страны, участники, плательщики, даты и ` +
  'номер вагона в эту комбинацию не входят.'

/** Группировка «вариантов» = `canonical_transition_key` среди прошлых связок единицы. */
export const TRANSITION_VARIANT_TOOLTIP =
  'Вариант определяется по паре кодов груза: код груза импортной ноги → код груза ' +
  `экспортной ноги. ${CARGO_KEY_RULE} Две связки с одинаковым названием груза, но ` +
  'разными кодами — это разные варианты.'

/** Группировка «грузов на вывоз» = `canonical_export_cargo_key`. */
export const EXPORT_CARGO_VARIANT_TOOLTIP =
  'Вариант определяется по коду груза экспортной ноги. ' + CARGO_KEY_RULE

/** Отбор популяции «связок с таким же кодом груза на ввоз». */
export const IMPORT_CARGO_POPULATION_TOOLTIP =
  'Отбор выполнен только по коду груза импортной ноги. ' + CARGO_KEY_RULE +
  ' Код груза экспортной ноги в отбор не входит, поэтому в списке примеров он может быть другим.'

/**
 * Что означает «исторические связки»: ЛЮБЫЕ более ранние сопоставленные пары
 * «импорт → экспорт», а не только прошлые календарные годы. С обновлением strict-prior
 * история кейса обрывается не 31 декабря прошлого года, а моментом самой экспортной
 * отправки, поэтому записи текущего года, зафиксированные раньше неё, в отбор входят.
 */
export const PRIOR_YEARS_TOOLTIP =
  'Учитываются сопоставленные пары «импорт → экспорт», зафиксированные до текущего кейса, ' +
  'включая более ранние записи того же года. Более поздние записи не учитываются.'

/**
 * Примечание к списку примеров популяции по импортному коду груза.
 *
 * Сервис отдаёт в строке один набор полей груза (при наличии — экспортной ноги), поэтому
 * колонки «Груз» и «Код груза» в примерах могут отличаться от кода, по которому шёл отбор.
 * Молча это оставить нельзя: заголовок обещал бы совпадение, которого в колонке не видно.
 */
export const IMPORT_CARGO_EXAMPLES_NOTE =
  'Отбор строк выполнен по коду груза импортной ноги. В колонках «Груз» и «Код груза» ' +
  'сервис показывает груз связки по данным экспортной ноги, поэтому значения в них ' +
  'могут отличаться от кода отбора.'

/** Коридор станций = четыре кода станций пары, а не пара стран. */
export const STATION_CORRIDOR_TOOLTIP =
  'Коридор станций определяется четырьмя станциями пары: станции отправления и назначения ' +
  'импортной ноги и станции отправления и назначения экспортной ноги.'

/** Окна всплеска: та же пара кодов груза, любые вагоны и контейнеры. */
export const BURST_POPULATION_TOOLTIP =
  'Похожими считаются сопоставленные пары «импорт → экспорт» с той же парой кодов груза ' +
  '«ввоз → вывоз». Номер вагона или контейнера в отбор не входит, поэтому в выборку попадают ' +
  'и другие физические единицы. Окно заканчивается за день до экспортной отправки текущего ' +
  'кейса, поэтому сам текущий кейс в счёт не входит.'

export const BURST_EXAMPLES_NOTE =
  'В списке — связки других вагонов и контейнеров тоже: отбор идёт по паре кодов груза, ' +
  'а не по физической единице.'

/**
 * ЕДИНСТВЕННОЕ компактное пояснение о природе исторических строк.
 *
 * ЗАЧЕМ. Строка исторического набора — это ПАРА записей корпуса: одна импортная и одна
 * экспортная, соединённые исторической логикой сопоставления. Пара «Плиты
 * древесностружечные (ЕТСНГ 122022) → Вагоны железнодорожные (ЕТСНГ 421034)» не означает,
 * что плиты физически превратились в вагон, и не означает, что эта пара — подтверждённый
 * скрытый транзит или кейс реестра «Кейсы». Она нужна только как фон, на котором видно,
 * насколько необычен ТЕКУЩИЙ кейс.
 *
 * ГДЕ ПОКАЗЫВАЕТСЯ. Ровно в одном месте — под открытым списком исторических строк
 * сценариев 1–5 (`EvidenceExamplesPanel`). Повторять его в каждой карточке причины
 * запрещено: методологический дисклеймер под каждым числом перестают читать.
 */
export const COMPARISON_BACKGROUND_NOTE =
  'Историческая связка — это пара записей «импорт → экспорт», сопоставленная аналитически: ' +
  'она служит фоном для сравнения и сама по себе не подтверждает скрытый транзит и не ' +
  'означает, что один груз физически превратился в другой.'

/* ── вагон, оформленный как перевозимый груз на своих осях ────────────────────── */

/**
 * Нейтральное распознавание формулировки перевозочного документа.
 *
 * Это НЕ признак порожности и не заменяет его: функция лишь узнаёт наименование груза,
 * при котором сам вагон едет как груз, чтобы карточка могла пояснить это словами вместо
 * того, чтобы читатель домысливал «порожний».
 */
export function isWagonAsCargoName(name: string | null | undefined): boolean {
  if (!name) return false
  // `\w` в JavaScript — это [A-Za-z0-9_], кириллицу он не покрывает, поэтому окончания
  // перечислены буквенным классом явно.
  return /перевозим[а-яё]*\s+на\s+своих\s+ос[а-яё]*/i.test(String(name))
}

export function isWagonAsCargo(row: Pick<AnomalyCaseRow, 'cargo' | 'cargoExport'>): boolean {
  return isWagonAsCargoName(row.cargo) || isWagonAsCargoName(row.cargoExport)
}

export const WAGON_AS_CARGO_TEXT =
  'В этой перевозке сам вагон оформлен как перевозимый груз на своих осях.'

export const WAGON_AS_CARGO_NO_WEIGHT_TEXT =
  'Масса груза в исходных данных не указана. Это не равнозначно массе 0 т и само по себе ' +
  'не подтверждает порожность вагона.'

/**
 * Почему карточка вообще не показывает статус «Порожний».
 *
 * В строке кейса аномалии (anomaly_cases_v3) нет ни поля, ни кода, ни категории перевозки,
 * которые система использует как явный признак порожности. Выводить статус эвристически —
 * из отсутствующей массы, из нуля или из наименования груза — запрещено, поэтому карточка
 * молчит о порожности, а не угадывает её.
 */
export const EMPTY_STATUS_UNAVAILABLE_TOOLTIP =
  'Статус «Порожний» в этой карточке не показывается: в данных кейса нет отдельного поля, ' +
  'кода или категории перевозки, подтверждающих порожность. Ни отсутствие массы, ни ' +
  'нулевая масса, ни наименование груза сами по себе порожность не подтверждают.'

/* ── масса груза: null ≠ 0 ≠ порожний ────────────────────────────────────────── */

export type AnomalyWeightKind =
  /** Ни одна из ног не несёт значения массы. */
  | 'missing_both'
  | 'missing_import'
  | 'missing_export'
  /** В payload действительно пришло число 0. */
  | 'numeric_zero'
  | 'invalid'
  | 'comparable'

export interface AnomalyWeightSemantics {
  kind: AnomalyWeightKind
  /** Строка «Детали кейса»: «Масса импорта → экспорта». */
  detailText: string
  /** Признак сценария: «Сравнение массы: …». */
  comparisonText: string
  /** Подсказка, снимающая подмену «нет данных» → «0 т» → «порожний». */
  tooltip: string
  /** Можно ли вообще посчитать разницу — статус признака при этом не меняется. */
  comparable: boolean
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** «0 т» показывается ТОЛЬКО когда в payload действительно лежит число 0. */
function sideText(v: number | null | undefined): string {
  if (v === null || v === undefined) return 'нет данных'
  if (!isNum(v)) return 'значение некорректно'
  if (v === 0) return '0 т'
  return `${v.toFixed(3)} т`
}

export const MISSING_WEIGHT_TOOLTIP =
  'Отсутствие массы не означает, что она равна 0 т, и не подтверждает порожность вагона ' +
  'или контейнера.'

export const NUMERIC_ZERO_TOOLTIP =
  'Нулевая масса груза может указывать на порожнюю перевозку, но статус порожности ' +
  'определяется также видом перевозки и данными перевозочного документа.'

/**
 * Anomaly-specific presenter массы. Читает те же поля, что и общий форматтер, и НЕ
 * меняет ни одного вычисления: разница веса, её процент и статус признака остаются
 * ровно теми, которые вернул `formatWeightDifference`.
 */
export function anomalyWeightSemantics(
  row: Pick<AnomalyCaseRow, 'importWeight' | 'exportWeight' | 'weightDiffPct'>,
): AnomalyWeightSemantics {
  const iw = row.importWeight ?? null
  const ew = row.exportWeight ?? null
  const w = formatWeightDifference(row)

  const pair = `${sideText(iw)} → ${sideText(ew)}`

  if (iw === null && ew === null) {
    return {
      kind: 'missing_both',
      detailText: 'данные отсутствуют',
      comparisonText: 'невозможно — масса груза отсутствует для обеих отправок',
      tooltip: `Масса груза не указана в исходных данных ни для импортной, ни для экспортной отправки. ${MISSING_WEIGHT_TOOLTIP}`,
      comparable: false,
    }
  }

  if (iw === null || ew === null) {
    const side = iw === null ? 'импортной' : 'экспортной'
    const known = iw === null ? ew : iw
    const knownSide = iw === null ? 'экспортной' : 'импортной'
    // Вторая нога может нести настоящий числовой ноль. Это ОТДЕЛЬНЫЙ факт, и он не должен
    // исчезнуть за сообщением об отсутствии массы у первой ноги.
    const zeroClause = known === 0 ? `, а для ${knownSide} указана как 0 т` : ''
    return {
      kind: iw === null ? 'missing_import' : 'missing_export',
      detailText: pair,
      comparisonText: `невозможно — масса груза не указана для ${side} отправки${zeroClause}`,
      tooltip: `Масса груза ${side} отправки не указана в исходных данных. ${MISSING_WEIGHT_TOOLTIP}` +
        (known === 0 ? ` Для ${knownSide} отправки указано числовое значение 0 т. ${NUMERIC_ZERO_TOOLTIP}` : ''),
      comparable: false,
    }
  }

  if (!isNum(iw) || !isNum(ew) || iw < 0 || ew < 0) {
    return {
      kind: 'invalid',
      detailText: pair,
      comparisonText: 'невозможно — значение массы указано некорректно',
      tooltip: 'Значение массы в исходных данных нечитаемо или отрицательно, поэтому сравнение не выполняется.',
      comparable: false,
    }
  }

  if (iw === 0 || ew === 0) {
    const which = iw === 0 && ew === 0 ? 'обеих отправок' : iw === 0 ? 'импортной отправки' : 'экспортной отправки'
    return {
      kind: 'numeric_zero',
      detailText: pair,
      comparisonText: `невозможно — масса груза ${which} указана как 0 т`,
      tooltip: `Масса груза ${which} указана как 0 т. ${NUMERIC_ZERO_TOOLTIP}`,
      comparable: false,
    }
  }

  return {
    kind: 'comparable',
    detailText: w.detailText,
    comparisonText: w.status === 'exact_match'
      ? `вес совпадает: ${pair}`
      : `разница ${w.displayValue} · ${pair}`,
    tooltip: w.tooltip,
    comparable: true,
  }
}

/* ── сценарий 5 ──────────────────────────────────────────────────────────────── */
//
// Раскрываемой справки о сценарии 5 в карточке больше нет — константа `SCENARIO_5_NOTE`
// удалена вместе с ней, а не оставлена «на всякий случай»: неиспользуемый пользовательский
// текст рано или поздно возвращается на экран без решения.
//
// Сам факт остаётся проверяемым и без текста на экране: обязательные признаки сценария 5
// живут в `SCENARIO_RULES[5]` (`anomaly-case-analysis.ts`) и совпадают с полем `scenarioName`
// самого кейса — «груз + станции + БИН + плательщик + вес + даты», без номера вагона.
// Соответствующий тест остался в `anomaly-semantics.test.ts`.
